import React, { useState, useEffect, useMemo } from 'react';
import { ethers } from 'ethers';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { useWeb3 } from '../../context/Web3Context';
import { useToast } from '../../context/ToastContext';
import DashboardLoader from '../Loading/DashboardLoader';
import { AnimVal, AnimNum, MCard, PageWrap, ProgressRing, Confetti, FloatingClaim } from '../UI';
import CONTRACTS from '../../contracts/addresses';
import IncomeABI from '../../contracts/IncomeABI.json';
import PoolsABI from '../../contracts/PoolsABI.json';
import { formatUSDT, formatAddress, getReferralLink, copyToClipboard, timeAgo, activityTypeLabel, activityTypeIcon, activityTypeClass } from '../../utils/helpers';

// ============================================
// RANK BADGE (Animated, glowing)
// ============================================
function RankBadge({ rank, name }) {
  if (!rank || rank === 0) return null;
  const icons = ['','⭐','⭐⭐','⭐⭐⭐','🌟','🌟🌟','🌟🌟🌟','💎','💎💎','👑'];
  const colors = ['','#FFD700','#FFD700','#FFD700','#00F5FF','#00F5FF','#00F5FF','#E040FB','#E040FB','#FF6B6B'];
  const glows = ['','rgba(255,215,0,0.4)','rgba(255,215,0,0.4)','rgba(255,215,0,0.5)','rgba(0,245,255,0.4)','rgba(0,245,255,0.4)','rgba(0,245,255,0.5)','rgba(224,64,251,0.4)','rgba(224,64,251,0.5)','rgba(255,107,107,0.5)'];
  const icon = icons[rank] || '🏆';
  const color = colors[rank] || '#FFD700';
  const glow = glows[rank] || 'rgba(255,215,0,0.4)';

  return (
    <motion.div style={{
      background:`linear-gradient(135deg, ${color}15, ${color}08)`,
      border:`1px solid ${color}40`,borderRadius:16,padding:'14px 20px',
      textAlign:'center',position:'relative',overflow:'hidden',marginBottom:4
    }} initial={{scale:0,opacity:0}} animate={{scale:1,opacity:1}} transition={{type:'spring',bounce:0.4,delay:0.1}}>
      {/* Shimmer overlay */}
      <motion.div style={{
        position:'absolute',top:0,left:'-100%',width:'100%',height:'100%',
        background:`linear-gradient(90deg, transparent, ${color}20, transparent)`,
      }} animate={{left:['−100%','200%']}} transition={{duration:3,repeat:Infinity,ease:'linear'}} />
      <motion.div style={{fontSize:'1.6rem',marginBottom:4,filter:`drop-shadow(0 0 8px ${glow})`}}
        animate={{scale:[1,1.05,1]}} transition={{duration:2,repeat:Infinity,ease:'easeInOut'}}>
        {icon}
      </motion.div>
      <div style={{fontFamily:'var(--font-h)',fontSize:'0.85rem',fontWeight:800,color,letterSpacing:'1.5px',textShadow:`0 0 12px ${glow}`}}>
        {name}
      </div>
      <div style={{fontSize:'0.6rem',color:'var(--text3)',textTransform:'uppercase',letterSpacing:'2px',marginTop:4}}>
        Permanent Rank
      </div>
    </motion.div>
  );
}

// ============================================
// WELCOME MODAL
// ============================================
function WelcomeModal({ userId, amount, onClose }) {
  return (
    <motion.div className="welcome-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <Confetti active={true} duration={8000} />
      <motion.div className="welcome-modal" initial={{ scale: 0.3, opacity: 0, y: 50 }} animate={{ scale: 1, opacity: 1, y: 0 }} transition={{ type: 'spring', bounce: 0.4, delay: 0.2 }}>
        <motion.div className="welcome-check" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', bounce: 0.6, delay: 0.5 }}>
          <svg width="80" height="80" viewBox="0 0 80 80"><motion.circle cx="40" cy="40" r="36" fill="none" stroke="#00E676" strokeWidth="3" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.8, delay: 0.6 }} /><motion.path d="M24 42 L35 53 L56 28" fill="none" stroke="#00E676" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.5, delay: 1.2 }} /></svg>
        </motion.div>
        <motion.h1 className="welcome-title" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.8 }}>
          🎉 WELCOME TO<br/><span className="grad-text">RICH FACTORY</span>
        </motion.h1>
        <motion.div className="welcome-id" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', delay: 1.0 }}>
          <div className="welcome-id-label">YOUR ID</div>
          <div className="welcome-id-number">#{userId}</div>
        </motion.div>
        <motion.div className="welcome-stats" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 1.2 }}>
          <div className="welcome-stat">
            <div className="welcome-stat-val">${amount}</div>
            <div className="welcome-stat-label">Invested</div>
          </div>
          <div className="welcome-stat">
            <div className="welcome-stat-val">{parseFloat(amount) >= 1000 ? '5%' : parseFloat(amount) >= 100 ? '4%' : '3%'}</div>
            <div className="welcome-stat-label">Monthly ROI</div>
          </div>
          <div className="welcome-stat">
            <div className="welcome-stat-val">{parseFloat(amount) >= 1000 ? '5x' : parseFloat(amount) >= 100 ? '4x' : '3x'}</div>
            <div className="welcome-stat-label">Cap</div>
          </div>
        </motion.div>
        <motion.p className="welcome-msg" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}>
          Your wealth manufacturing journey begins now! Share your referral link and start building your team.
        </motion.p>
        <motion.button className="btn btn-gold btn-full welcome-go" onClick={onClose}
          initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 1.6 }}
          whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
          🚀 LET'S GO!
        </motion.button>
      </motion.div>
    </motion.div>
  );
}

// ============================================
// GAUGE
// ============================================
function Gauge({ earned, cap }) {
  const e = parseFloat(ethers.utils.formatUnits(earned, 18));
  const c = parseFloat(ethers.utils.formatUnits(cap, 18));
  const pct = c > 0 ? Math.min((e / c) * 100, 100) : 0;
  const arcLen = 251, offset = arcLen - (arcLen * pct / 100);
  const color = pct < 50 ? '#00F5FF' : pct < 80 ? '#FFB800' : '#FF4757';
  return (<div className="gauge-wrap"><svg className="gauge-svg" viewBox="0 0 180 110"><path d="M 10 100 A 80 80 0 0 1 170 100" className="gauge-bg" /><motion.path d="M 10 100 A 80 80 0 0 1 170 100" className="gauge-fill" style={{ '--arc': offset, stroke: color, filter: `drop-shadow(0 0 8px ${color})` }} initial={{ strokeDashoffset: 251 }} animate={{ strokeDashoffset: offset }} transition={{ duration: 1.8, ease: 'easeOut', delay: 0.3 }} /></svg><div className="gauge-center"><div className="gauge-pct">{pct.toFixed(1)}%</div><div className="gauge-sub">Cap Used</div></div></div>);
}

// ============================================
// HOMEPAGE
// ============================================
export default function HomePage() {
  const { contracts, userId, account, signer } = useWeb3();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [d, setD] = useState(null);
  const [acts, setActs] = useState([]);
  const [pendingROI, setPendingROI] = useState(ethers.BigNumber.from(0));
  const [claiming, setClaiming] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const [wAmt, setWAmt] = useState('');
  const [wRaw, setWRaw] = useState(null);
  const [wOpen, setWOpen] = useState(false);
  const [wLoading, setWLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeAmt, setWelcomeAmt] = useState('0');
  const [royaltyClaimable, setRoyaltyClaimable] = useState(ethers.BigNumber.from(0));
  const [royaltyRounds, setRoyaltyRounds] = useState(0);
  const [claimingRoyalty, setClaimingRoyalty] = useState(false);
  const [rfPrice, setRfPrice] = useState(0);
  // V5: Rank
  const [userRank, setUserRank] = useState(0);
  const [userRankName, setUserRankName] = useState('');
  // V6: Royal pool eligibility
  const [royalPoolEligible, setRoyalPoolEligible] = useState(false);
  const [actPage, setActPage] = useState(1);
  const [isAdminId, setIsAdminId] = useState(false);
  const ACTS_PER_PAGE = 10;

  useEffect(() => {
    if (params.get('welcome') === 'true') {
      setShowWelcome(true);
      setWelcomeAmt(params.get('amount') || '0');
    }
  }, [params]);

  const closeWelcome = () => { setShowWelcome(false); setParams({}, { replace: true }); };

  useEffect(() => {
    if (!contracts.storage || !userId) return;
    let alive = true;
    (async () => {
      try {
        const [ui, inc, di, avail] = await Promise.all([
          contracts.storage.getUserInfo(userId), contracts.storage.getIncomeInfo(userId),
          contracts.storage.getDirectInfo(userId), contracts.storage.getAvailableBalance(userId)
        ]);
        // RF coin price
        let price = 0;
        try { const p = await contracts.storage.rewardTokenPrice(); price = parseFloat(ethers.utils.formatUnits(p, 18)); } catch {}
        let pr = ethers.BigNumber.from(0);
        try { const ic = new ethers.Contract(CONTRACTS.INCOME, IncomeABI, signer); pr = await ic.getPendingROI(userId); } catch {}
        let capTotal = ethers.BigNumber.from(0), earnedTotal = ethers.BigNumber.from(0);
        try {
          const cnt = await contracts.storage.getInvestmentCount(userId);
          for (let i = 0; i < cnt.toNumber(); i++) {
            const inv = await contracts.storage.getInvestment(userId, i);
            capTotal = capTotal.add(inv[4]); earnedTotal = earnedTotal.add(inv[3]);
          }
        } catch {}
        // Future staking
        let fsAcc = ethers.BigNumber.from(0), fsIncome = ethers.BigNumber.from(0);
        try {
          const bd = await contracts.storage.getBinaryDataView(userId);
          fsAcc = bd[2]; fsIncome = bd[4];
        } catch {}
        // Royalty claimable
        let rc = ethers.BigNumber.from(0), rr = 0;
        try {
          const pc = new ethers.Contract(CONTRACTS.POOLS, PoolsABI, signer);
          const res = await pc.getRoyaltyClaimable(userId);
          rc = res[0]; rr = res[1].toNumber();
        } catch {}
        // V5: Rank (permanent)
        let rk = 0, rn = '';
        try {
          const pc = new ethers.Contract(CONTRACTS.POOLS, PoolsABI, signer);
          const rankRes = await pc.getUserRank(userId);
          rk = rankRes[0].toNumber(); rn = rankRes[1];
        } catch {}
        // V6: Royal pool eligibility
        let rpEligible = false;
        try {
          const pc = new ethers.Contract(CONTRACTS.POOLS, PoolsABI, signer);
          rpEligible = await pc.isRoyalPoolEligible(userId);
        } catch {}
        let a = [];
        try { const r = await contracts.storage.getRecentActivities(userId, 100); if (r.amounts) for (let i = 0; i < r.amounts.length; i++) a.push({ amount: r.amounts[i], timestamp: r.timestamps[i], fromId: r.fromIds[i], type: r.types[i] }); } catch {}
        // V6: Check admin status for cap display
        let adminId = false;
        try { adminId = await contracts.storage.isAdminUser(userId); } catch {}
        if (alive) {
          setD({ sponsorId: ui[1], totalInvestment: ui[3], active: ui[5],
            roi: inc[0], binary: inc[1], referral: inc[2], level: inc[3], leadership: inc[4],
            mentorship: inc[5], futureStk: inc[6], royalty: inc[7], royalPool: inc[8],
            tokenReward: inc[9], withdrawn: inc[10],
            dr: di[0], qd: di[1], tc: di[2], db: di[3], available: avail, capTotal, earnedTotal,
            fsAcc, fsIncome });
          setPendingROI(pr); setActs(a.reverse()); setLoading(false);
          setRoyaltyClaimable(rc); setRoyaltyRounds(rr);
          setRfPrice(price);
          setUserRank(rk); setUserRankName(rn);
          setRoyalPoolEligible(rpEligible);
          setIsAdminId(adminId);
        }
      } catch (e) { console.error(e); if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [contracts.storage, userId, signer]);

  const claimROI = async () => { setClaiming(true); try { const ic = new ethers.Contract(CONTRACTS.INCOME, IncomeABI, signer); toast.info('Claiming ROI...'); const tx = await ic.processROI(userId); await tx.wait(); toast.success('ROI claimed! 🎉'); setConfetti(true); setTimeout(() => window.location.reload(), 3000); } catch (e) { toast.error(e.reason || 'Claim failed'); } setClaiming(false); };

  const handleW = async () => {
    let sendAmt;
    if (wRaw) { sendAmt = wRaw; }
    else { if (!wAmt || parseFloat(wAmt) <= 0) { toast.error('Enter amount'); return; } sendAmt = ethers.utils.parseUnits(wAmt, 18); }
    setWLoading(true);
    try { toast.info('Withdrawing...'); const tx = await contracts.logic.withdraw(sendAmt); await tx.wait(); toast.success('Withdrawn!'); setConfetti(true); setTimeout(() => window.location.reload(), 3000); } catch (e) { toast.error(e.reason || 'Failed'); }
    setWLoading(false);
  };

  const setMaxW = () => { if (d?.available) { setWRaw(d.available); setWAmt(ethers.utils.formatUnits(d.available, 18)); } };

  const claimRoyalty = async () => {
    setClaimingRoyalty(true);
    try { const pc = new ethers.Contract(CONTRACTS.POOLS, PoolsABI, signer); toast.info('Claiming royalty...'); const tx = await pc.claimRoyaltyIncome(); await tx.wait(); toast.success('Royalty claimed! 🎉'); setConfetti(true); setTimeout(() => window.location.reload(), 3000); } catch (e) { toast.error(e.reason || 'Claim failed'); }
    setClaimingRoyalty(false);
  };

  const copy = async (pos) => { await copyToClipboard(getReferralLink(userId, pos)); toast.success(`${pos === 0 ? 'Left' : 'Right'} link copied!`); };
  const pendingFloat = useMemo(() => { try { return parseFloat(ethers.utils.formatUnits(pendingROI, 18)); } catch { return 0; } }, [pendingROI]);
  const tokenRewardFloat = useMemo(() => { try { return parseFloat(ethers.utils.formatUnits(d?.tokenReward || 0, 18)); } catch { return 0; } }, [d?.tokenReward]);

  if (loading) return <DashboardLoader />;
  if (!d) return <PageWrap><MCard><p style={{ textAlign: 'center', color: 'var(--text3)' }}>Unable to load</p></MCard></PageWrap>;
  const total = [d.roi, d.binary, d.referral, d.level, d.leadership, d.mentorship, d.futureStk, d.royalty, d.royalPool].reduce((a, b) => a.add(b), ethers.BigNumber.from(0));
  // V6: Admin IDs show 5x cap instead of actual (dummy 10000x)
  const displayCap = isAdminId ? d.totalInvestment.mul(5) : d.capTotal;
  const displayEarned = isAdminId ? ethers.BigNumber.from(0) : d.earnedTotal;
  const capPct = displayCap.gt(0) ? parseFloat(ethers.utils.formatUnits(displayEarned, 18)) / parseFloat(ethers.utils.formatUnits(displayCap, 18)) * 100 : 0;

  return (<PageWrap>
    <AnimatePresence>{showWelcome && <WelcomeModal userId={userId} amount={welcomeAmt} onClose={closeWelcome} />}</AnimatePresence>
    <Confetti active={confetti} />
    <AnimatePresence><FloatingClaim amount={pendingFloat} onClick={claimROI} /></AnimatePresence>

    {/* === V5: RANK BADGE === */}
    <RankBadge rank={userRank} name={userRankName} />

    {/* === PROFILE HERO === */}
    <MCard className="hero-card" delay={0}>
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <ProgressRing percent={Math.min(capPct, 100)} size={68} strokeWidth={3} color={capPct < 50 ? '#00F5FF' : capPct < 80 ? '#FFB800' : '#FF4757'}>
          <img src="/coin.png" alt="" style={{ width: 36, height: 36 }} />
        </ProgressRing>
        <motion.div style={{ fontFamily: 'var(--font-h)', fontSize: '2rem', fontWeight: 900, marginTop: 8 }} className="grad-text" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', bounce: 0.4, delay: 0.2 }}>#{userId}</motion.div>
        <div style={{ fontFamily: 'var(--font-m)', fontSize: '0.68rem', color: 'var(--text3)', marginTop: 2 }}>{formatAddress(account)}</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 6 }}>
          <span className="badge badge-gr">● Active</span>
          <span style={{ fontSize: '0.72rem', color: 'var(--text2)' }}>Upline <span style={{ color: 'var(--cyan)', fontWeight: 700, fontFamily: 'var(--font-m)' }}>#{d.sponsorId?.toString()}</span></span>
        </div>
        <div className="g3" style={{ marginTop: 18 }}>
          <div className="fcc"><AnimVal value={d.totalInvestment} className="val val-lg val-gold" /><div className="label">Invested</div></div>
          <div className="fcc"><AnimVal value={total} className="val val-lg val-green" /><div className="label">Total Earned</div></div>
          <div className="fcc"><AnimVal value={d.available} className="val val-lg val-cyan" /><div className="label">Available</div></div>
        </div>
      </div>
    </MCard>

    {/* === RF COIN PRICE === */}
    <MCard delay={0.04}>
      <div className="fb">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/coin.png" alt="RF" style={{ width: 28, height: 28 }} />
          <div>
            <div style={{ fontSize: '0.68rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '1px' }}>RF Coin Price</div>
            <div style={{ fontFamily: 'var(--font-h)', fontSize: '1.2rem', fontWeight: 800, color: 'var(--gold)' }}>${rfPrice > 0 ? rfPrice.toFixed(4) : '0.0000'}</div>
          </div>
        </div>
        <span className="badge badge-g">LIVE</span>
      </div>
    </MCard>

    {/* === CEILING GAUGE === */}
    <MCard delay={0.06}><Gauge earned={displayEarned} cap={displayCap} />
      <div className="fb" style={{ marginTop: 4, padding: '0 10px' }}>
        <div className="fcc"><AnimVal value={displayEarned} className="val val-sm val-green" /><div className="label">Earned</div></div>
        <div className="fcc"><AnimVal value={displayCap} className="val val-sm val-gold" /><div className="label">Cap Limit</div></div>
        <div className="fcc"><AnimVal value={displayCap.sub(displayEarned.gt(displayCap) ? displayCap : displayEarned)} className="val val-sm val-cyan" /><div className="label">Remaining</div></div>
      </div>
    </MCard>

    {/* === PENDING ROI === */}
    <MCard className="conic-card" delay={0.12} style={{ textAlign: 'center', padding: '28px 22px' }}>
      <div className="roi-label">Pending ROI</div>
      <motion.div className="roi-val" initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', delay: 0.3 }}><span>$</span>{formatUSDT(pendingROI)}</motion.div>
      <div className="roi-sub" style={{ marginTop: 8 }}>Claims ROI + distributes Level & Direct ROI income</div>
      <motion.button className="btn btn-primary btn-full" onClick={claimROI} disabled={claiming} style={{ padding: 16, marginTop: 12 }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>{claiming ? '⏳ Processing...' : '📈 Claim ROI'}</motion.button>
    </MCard>

    {/* === ROYALTY CLAIM === */}
    {royaltyRounds > 0 && royaltyClaimable.gt(0) && (
      <MCard className="conic-card" delay={0.14} style={{ textAlign: 'center', padding: '22px' }}>
        <div style={{ fontSize: '0.6rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: 6 }}>Royalty Income Available</div>
        <motion.div style={{ fontFamily: 'var(--font-h)', fontSize: '1.8rem', fontWeight: 900, color: 'var(--gold)' }} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', delay: 0.3 }}>${formatUSDT(royaltyClaimable)}</motion.div>
        <div style={{ fontSize: '0.68rem', color: 'var(--text2)', margin: '8px 0 12px' }}>{royaltyRounds} unclaimed round{royaltyRounds > 1 ? 's' : ''}</div>
        <motion.button className="btn btn-gold btn-full" onClick={claimRoyalty} disabled={claimingRoyalty} style={{ padding: 14 }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>{claimingRoyalty ? '⏳ Claiming...' : '💎 Claim Royalty'}</motion.button>
      </MCard>
    )}

    {/* === REFERRAL LINKS === */}
    {[0, 1].map(p => (<MCard key={p} className="card" delay={0.18 + p * 0.06} onClick={() => copy(p)} style={{ cursor: 'pointer' }}><div className="ref-card" style={{ margin: 0, border: 0, background: 'transparent', padding: 0 }}><div className={`ref-dir ${p === 0 ? 'left' : 'right'}`}>{p === 0 ? '◀' : '▶'}</div><div className="ref-info"><div className="ref-title">{p === 0 ? 'Left' : 'Right'} Referral Link</div><div className="ref-url">{getReferralLink(userId, p)}</div></div><motion.div className="ref-copy-btn" whileTap={{ scale: 0.9 }}>📋 COPY</motion.div></div></MCard>))}

    {/* === QUICK STATS === */}
    <div className="g2">
      <MCard delay={0.3} style={{ textAlign: 'center', padding: '18px 14px' }}><AnimNum value={d.dr} className="val val-lg" /><div className="label">Direct Referrals</div></MCard>
      <MCard delay={0.36} style={{ textAlign: 'center', padding: '18px 14px' }}><AnimNum value={d.tc} className="val val-lg" /><div className="label">Team Members</div></MCard>
    </div>

    {/* === INCOME BREAKDOWN === */}
    <div className="stitle">Income Breakdown</div>
    <div className="g2">
      {[
        { l: 'ROI Income', v: d.roi, i: '📈', cls: 'bounce-icon' },
        { l: 'Binary', v: d.binary, i: '⚡', cls: 'pulse-icon' },
        { l: 'Referral', v: d.referral, i: '🤝' },
        { l: 'Level', v: d.level, i: '📊' },
        { l: 'Direct ROI', v: d.leadership, i: '👑', cls: 'pulse-icon' },
        { l: 'Mentorship', v: d.mentorship, i: '🎓' },
        { l: 'Royalty Pool', v: d.royalty, i: '💎' },
        ...(royalPoolEligible ? [{ l: 'Royal Pool', v: d.royalPool, i: '🏛️' }] : [])
      ].map((x, i) => (<MCard key={i} className="icard" delay={0.4 + i * 0.04} style={{ margin: 0 }}><div className={`anim-icon ${x.cls || ''}`} style={{ fontSize: '1.15rem', marginBottom: 5 }}>{x.i}</div><div className="icard-label">{x.l}</div><AnimVal value={x.v} className="icard-val" /></MCard>))}
      <MCard className="icard" delay={0.74} style={{ margin: 0 }}>
        <div className="anim-icon spin-icon" style={{ fontSize: '1.15rem', marginBottom: 5 }}>🔒</div>
        <div className="icard-label">FS Accumulated</div>
        <AnimVal value={d.fsAcc} className="icard-val" color="var(--purple)" />
      </MCard>
      <MCard className="icard" delay={0.78} style={{ margin: 0 }}>
        <div className="anim-icon bounce-icon" style={{ fontSize: '1.15rem', marginBottom: 5 }}>🔓</div>
        <div className="icard-label">FS Income</div>
        <AnimVal value={d.futureStk} className="icard-val" color="var(--green)" />
      </MCard>
      <MCard className="icard" delay={0.82} style={{ margin: 0 }}>
        <div className="anim-icon pulse-icon" style={{ fontSize: '1.15rem', marginBottom: 5 }}>🪙</div>
        <div className="icard-label">Token Reward</div>
        <span className="icard-val" style={{ color: 'var(--green)' }}>{tokenRewardFloat.toFixed(2)} RF</span>
      </MCard>
      <MCard className="icard" delay={0.86} style={{ margin: 0 }}>
        <div className="anim-icon" style={{ fontSize: '1.15rem', marginBottom: 5 }}>💸</div>
        <div className="icard-label">Withdrawn</div>
        <AnimVal value={d.withdrawn} className="icard-val" />
      </MCard>
    </div>

    {/* === WITHDRAW === */}
    <div className="stitle">Withdraw</div>
    <MCard delay={0.9}>
      <div className="fb" style={{ marginBottom: 14 }}><span style={{ fontSize: '0.82rem', color: 'var(--text2)' }}>Available Balance</span><AnimVal value={d.available} className="val val-md val-cyan" /></div>
      <AnimatePresence>
        {wOpen ? (<motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ display: 'flex', flexDirection: 'column', gap: 12, overflow: 'hidden' }}>
          <div style={{ position: 'relative' }}>
            <input type="text" placeholder="Enter amount" value={wAmt} onChange={e => { setWAmt(e.target.value); setWRaw(null); }} style={{ paddingRight: 60 }} />
            <motion.button onClick={setMaxW} whileTap={{ scale: 0.9 }} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,245,255,0.15)', border: '1px solid rgba(0,245,255,0.3)', borderRadius: 8, padding: '4px 12px', color: 'var(--cyan)', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}>MAX</motion.button>
          </div>
          <div className="wd-note">10% deduction: 5% liquidity + 5% investors. You'll receive 5% in RF tokens.</div>
          <div className="fr">
            <motion.button className="btn btn-primary btn-full" onClick={handleW} disabled={wLoading} whileTap={{ scale: 0.97 }}>{wLoading ? '⏳...' : '💸 Withdraw'}</motion.button>
            <motion.button className="btn btn-ghost" onClick={() => { setWOpen(false); setWRaw(null); }} style={{ minWidth: 80 }} whileTap={{ scale: 0.95 }}>Cancel</motion.button>
          </div>
        </motion.div>
        ) : (<motion.button className="btn btn-primary btn-full" onClick={() => setWOpen(true)} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>💸 Withdraw Funds</motion.button>)}
      </AnimatePresence>
    </MCard>

    {/* === ACTIVITY === */}
    <div className="stitle">Recent Activity</div>
    <MCard delay={0.95}>
      {acts.length === 0 ? <p style={{ textAlign: 'center', color: 'var(--text3)', padding: 20 }}>No activity yet</p> :
        <>
          {acts.slice(0, actPage * ACTS_PER_PAGE).map((a, i) => (<motion.div key={i} className="act-item" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i < ACTS_PER_PAGE ? 0.1 * i : 0 }}><div className={`act-icon ${activityTypeClass(a.type)}`}>{activityTypeIcon(a.type)}</div><div className="act-det"><div className="act-type">{activityTypeLabel(a.type)}</div><div className="act-time">{timeAgo(a.timestamp)}</div></div><div className={`act-amt ${Number(a.type) === 3 ? 'neg' : 'pos'}`}>{Number(a.type) === 3 ? '-' : '+'}${formatUSDT(a.amount)}</div></motion.div>))}
          {acts.length > actPage * ACTS_PER_PAGE && (
            <motion.button className="btn btn-ghost btn-full" onClick={() => setActPage(p => p + 1)} style={{ marginTop: 10, padding: 12, fontSize: '0.78rem' }} whileTap={{ scale: 0.97 }}>
              Load More ({acts.length - actPage * ACTS_PER_PAGE} remaining)
            </motion.button>
          )}
          {acts.length <= actPage * ACTS_PER_PAGE && acts.length > ACTS_PER_PAGE && (
            <div style={{ textAlign: 'center', fontSize: '0.68rem', color: 'var(--text3)', marginTop: 8 }}>All {acts.length} activities loaded</div>
          )}
        </>}
    </MCard>
  </PageWrap>);
}
